import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { getPendingSizeInput, removePendingSizeInput, savePendingSizeInput } from '../constant/local-storage';

export const PENDING_SIZE_INPUT_QUERY_KEY = ['pdp', 'pendingSizeInput'] as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- 체형 입력 필드 키가 동적이라 any 사용
export type PendingSizeInputValues = Record<string, any>;

const readAndClearPendingFromStorage = (): PendingSizeInputValues | null => {
  if (typeof window === 'undefined') return null;
  const data = getPendingSizeInput() ?? null;
  removePendingSizeInput();
  return data;
};

export const usePendingSizeInput = () => {
  const queryClient = useQueryClient();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const query = useQuery(PENDING_SIZE_INPUT_QUERY_KEY, readAndClearPendingFromStorage, {
    enabled: isMounted,
    staleTime: Infinity,
    cacheTime: 5 * 60 * 1000,
  });

  const setPending = useCallback(
    (values: PendingSizeInputValues) => {
      savePendingSizeInput(values);
      queryClient.setQueryData(PENDING_SIZE_INPUT_QUERY_KEY, values);
    },
    [queryClient],
  );

  const clearPending = useCallback(() => {
    removePendingSizeInput();
    queryClient.setQueryData(PENDING_SIZE_INPUT_QUERY_KEY, null);
  }, [queryClient]);

  const consumePending = useCallback((): PendingSizeInputValues | null => {
    const currentData = queryClient.getQueryData<PendingSizeInputValues | null>(PENDING_SIZE_INPUT_QUERY_KEY);
    const dataToConsume = currentData ?? readAndClearPendingFromStorage();
    removePendingSizeInput();
    queryClient.setQueryData(PENDING_SIZE_INPUT_QUERY_KEY, null);
    return dataToConsume;
  }, [queryClient]);

  return {
    data: query.data === undefined ? undefined : (query.data ?? null),
    isLoading: !isMounted || query.isLoading,
    setPending,
    clearPending,
    consumePending,
  };
};
